import { useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import {
  useStoreDashboardData,
  DashboardDataRequestError,
} from "../../hooks/useStoreDashboardData";
import { DashboardRenderer } from "../DashboardRenderer";
import { SectionCallout } from "../sections/SectionCallout";
import { SectionSkeleton } from "../SectionSkeleton";
import type { DashboardSpec, Section } from "../types";

/** The three per-store sub-tabs, selected by the `view` query param.
 *  Absent/invalid → overview. */
export type StoreViewTab = "overview" | "triage" | "inventory";

const TAB_LABELS: Record<StoreViewTab, string> = {
  overview: "Overview",
  triage: "Triage",
  inventory: "Inventory",
};

type TabSectionRef = { id: string; head?: number };

/** Per-tab composition — ordered section ids consumed from the store payload.
 *  The server owns the panel set: ids missing from the payload are skipped,
 *  and a `head` override clones the section with that visible head. The
 *  `identity` / `risk_kpis` / `data_kpis` sections render in the shell above
 *  the tab switcher, so no tab lists them. */
const TAB_COMPOSITION: Record<StoreViewTab, TabSectionRef[]> = {
  overview: [
    { id: "store_lead" },
    { id: "red_flag_callout" },
    { id: "tier_bar" },
    { id: "category_health" },
    { id: "bad_assets" },
    { id: "oldest_assets" },
    { id: "red_flags" },
    { id: "defect_programs" },
  ],
  triage: [
    { id: "triage_lead" },
    { id: "rank_kpis" },
    { id: "red_flags" },
    { id: "bad_assets" },
    { id: "defect_programs" },
    { id: "vs_portfolio" },
    { id: "category_health", head: 10 },
    { id: "oldest_assets" },
  ],
  inventory: [
    { id: "asset_inventory" },
    { id: "red_flags", head: 5 },
    { id: "defect_programs" },
    { id: "category_rollup" },
  ],
};

function parseTab(raw: string | null): StoreViewTab {
  return raw === "triage" || raw === "inventory" ? raw : "overview";
}

/** Wrap a list of sections as a renderable spec slice (general-tab idiom). */
function slice(sections: Section[], generatedAt: string): DashboardSpec {
  return {
    title: "",
    question: "",
    sections,
    followups: [],
    generated_at: generatedAt,
  };
}

/** A `head` override clones the section; only the head-bearing kinds take it. */
function withHead(section: Section, head: number | undefined): Section {
  if (head == null) return section;
  if (
    section.kind === "table" ||
    section.kind === "bar_list" ||
    section.kind === "filter_table"
  ) {
    return { ...section, head };
  }
  return section;
}

/** Compact micro-label fact (label above, value below) for the identity strip. */
function HeaderFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-sm tabular-nums">{value}</span>
    </div>
  );
}

interface StoreViewProps {
  clientId: string;
  storeNumber: string;
  /** The clicked label from the panel that opened this store — header text
   *  while the fill is loading (the server's spec.title takes over on data). */
  labelHint?: string;
  onBack: () => void;
}

/**
 * Per-store detail view: back link, store header (server-sent title +
 * identity fact strip), the shell KPI rows, and the Overview | Triage |
 * Inventory sub-tabs composed by section id from one store-scoped
 * `dashboard-data` fill.
 */
export function StoreView({ clientId, storeNumber, labelHint, onBack }: StoreViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get("view"));

  const { data: spec, isPending, isError, error, refetch } =
    useStoreDashboardData(clientId, storeNumber);

  // Tab switches REPLACE the history entry — browser Back always zooms out to
  // the general view rather than replaying tab flips.
  const setTab = (next: StoreViewTab) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "overview") params.delete("view");
        else params.set("view", next);
        return params;
      },
      { replace: true },
    );
  };

  // A store payload is one whose sections carry ids (v2 contract). The live v1
  // fn ignores storeNumber and returns the GENERAL fill — never render that as
  // if it were store data.
  const isStorePayload = !!spec && spec.sections.some((s) => s.id != null);

  const sectionsById = useMemo(() => {
    const map = new Map<string, Section>();
    if (!spec) return map;
    for (const section of spec.sections) {
      if (section.id != null) map.set(section.id, section);
    }
    return map;
  }, [spec]);

  const identity = sectionsById.get("identity");
  const shellKpiSections = ["risk_kpis", "data_kpis"].flatMap((id) => {
    const section = sectionsById.get(id);
    return section ? [section] : [];
  });
  const tabSections = TAB_COMPOSITION[tab].flatMap((ref) => {
    const section = sectionsById.get(ref.id);
    return section ? [withHead(section, ref.head)] : [];
  });

  const title =
    (isStorePayload ? spec?.title : "") || labelHint || `#${storeNumber}`;

  let body: ReactNode;
  if (isPending) {
    body = (
      <div className="space-y-3" role="status" aria-label="Loading the store view">
        <SectionSkeleton shape="kpi" />
        <SectionSkeleton shape="answer" />
        <SectionSkeleton shape="table" />
      </div>
    );
  } else if (isError || !spec) {
    const notFound =
      error instanceof DashboardDataRequestError && error.status === 404;
    body = notFound ? (
      <SectionCallout
        severity="warn"
        body={`Store #${storeNumber} wasn't found in this portfolio.`}
      />
    ) : (
      <div className="space-y-3">
        <SectionCallout
          severity="warn"
          body={`Couldn't load the store view${
            error instanceof Error ? ` — ${error.message}` : ""
          }.`}
        />
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  } else if (!isStorePayload) {
    body = (
      <div className="space-y-3">
        <SectionCallout
          severity="warn"
          body="The per-store view needs the upgraded dashboard-data service, which isn't deployed yet."
        />
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  } else {
    body = (
      <>
        {shellKpiSections.length > 0 ? (
          <DashboardRenderer
            spec={slice(shellKpiSections, spec.generated_at)}
            showSaveButtons={false}
          />
        ) : null}

        <div
          role="group"
          aria-label="Store view tab"
          className="inline-flex items-center rounded-md bg-muted/40 p-1"
        >
          {(Object.keys(TAB_LABELS) as StoreViewTab[]).map((key) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={active}
                onClick={() => setTab(key)}
                className={cn(
                  "rounded-sm px-3 py-1.5 text-sm font-medium transition-all",
                  "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  active
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {TAB_LABELS[key]}
              </button>
            );
          })}
        </div>

        <DashboardRenderer
          spec={slice(tabSections, spec.generated_at)}
          showSaveButtons={false}
        />
      </>
    );
  }

  return (
    <div className="space-y-4">
      <header className="space-y-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="gap-1.5 -ml-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          All stores
        </Button>
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        {identity?.kind === "kpi_row" && identity.items.length > 0 ? (
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {identity.items.map((item) => (
              <HeaderFact key={item.label} label={item.label} value={item.value} />
            ))}
          </div>
        ) : null}
      </header>
      {body}
    </div>
  );
}
